// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Starter Custom Code - >
import getColor from "@insite/mobius/utilities/getColor";
import Link from "@store/MobiusOverrides/Link/Link";
import constants from "@store/constants";
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
import useCanSeePrices from "@store/Framework/Store/Data/Accounts/Hooks/useCanSeePrices";
import useAllowZeroPricing from "@store/Framework/Store/Data/Products/ProductsHooks/useAllowZeroPricing";
import useIsProductRestricted from "@store/Framework/Store/Data/Products/ProductsHooks/useIsProductRestricted";
import { productHasPrice } from "@store/Framework/Store/Data/Products/ProductsSelectors";
import PromotionalTag from "@store/Library/Components/Common/PromotionalTag";
import BuyingListFeedbackModal from "@store/Library/Widgets/BuyingList/BuyingListFeedbackModal";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";

const { userCannotSeePricesMessage } = constants;
// <--- Starter Custom Code

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: ProductPriceStyles;
    overrideStyles?: ProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    // Starter Custom Code
    priceDetailWrapper?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    tagWrapper?: InjectableCss;
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    // Starter Custom Code
    saleLabelText?: TypographyPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 100%;
            height: 100%;
            padding: ${pxToRem(8, 0)};
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
            justify-content: flex-start;
            position: relative;
        `,
    },
    price: {
        // Store Custom Code - >
        tagWrapper: {
            css: css`
                display: flex;
                gap: 0.5rem;
                align-items: center;
                justify-content: flex-start;
                flex-wrap: wrap;
            `,
        },
        // <-- Bunzl Custom Code
        priceText: {
            // Store Custom Code
            color: "text.main",
            css: css`
                position: relative;
                // Bunzl Custom Code
                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.large};
                    font-weight: ${theme.design.typography.weight.bold};
                `}
            `,
        },
        errorText: {
            css: css`
                font-weight: ${({ theme }) => theme.design.typography.weight.medium};
            `,
        },
        realTimeText: {
            // Store Custom Code
            color: "text.main",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: ${pxToRem(25)};
                font-weight: ${({ theme }) => theme.design.typography.weight.bold};
                font-size: ${({ theme }) => theme.design.typography.size.large};
                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            // Theme functions seem to break override styles, so we have to enter values manually
            unitOfMeasureText: {
                color: "primary",
                css: css`
                    white-space: nowrap;
                    font-size: 1.25rem;
                    font-weight: 500;
                `,
            },
        },
        signInText: {
            color: "primary",
            css: css`
                min-height: ${pxToRem(25)};
                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.smaller};
                    font-weight: ${theme.design.typography.weight.medium};
                `}
            `,
        },

        unitOfMeasureText: {
            color: "primary",
            css: css`
                white-space: nowrap;
                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.smaller};
                    font-weight: ${theme.design.typography.weight.base};
                `}
            `,
        },
        vatLabelText: {
            css: css`
                font-size: ${pxToRem(12)};
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: ${pxToRem(5)};
            ${wrapInContainerStyles}
            position: relative;
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: ${pxToRem(25)};

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            css: css`
                font-size: ${pxToRem(12)};
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: ${pxToRem(8)};
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        css: css`
            color: ${getColor("text.milanoRed")};
        `,
        italic: true,
        size: pxToRem(12),
    },
    quoteMessage: {
        text: { weight: "bold" },
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = true, // Starter Custom Code
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    overrideStyles,
}: Props) => {
    const styles = useExpandProps(productPriceStyles, extendedStyles, overrideStyles); // Starter Custom Code
    // Starter Custom Code - >
    const canUserSeePrices = useCanSeePrices();
    // Store Custom Code - >
    const isProductRestricted = useIsProductRestricted("product" in pricingData ? pricingData.product : undefined);
    // <--- Bunzl Custom Code
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();
    const allowZeroPricing = useAllowZeroPricing(pricingData);
    const [buyingListFeedbackModalIsOpen, setBuyingListFeedbackModalIsOpen] = React.useState(false);
    // <--- Starter Custom Code

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    // Starter Removed Code
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};
    // Starter Custom Code - >
    if (!isSignedInAndhasToBeSignedIn || (pricingData as ProductContextModel).product?.isVariantParent) {
        return null;
    }
    // <-- Starter Custom Code
    // Store Custom Code - >
    const productName = "product" in pricingData ? pricingData.product.productTitle : pricingData.shortDescription;
    if (isProductRestricted) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <BuyingListFeedbackModal
                    productOveride={"product" in pricingData ? pricingData.product : undefined}
                    productName={productName}
                    isOpen={buyingListFeedbackModalIsOpen}
                    handleClose={() => setBuyingListFeedbackModalIsOpen(false)}
                />
                <Link
                    onClick={() => setBuyingListFeedbackModalIsOpen(true)}
                    data-test-selector="productPrice_PricingMessage"
                >
                    {siteMessage("BuyingList_ProductIsRestricted")}
                </Link>
            </StyledWrapper>
        );
    }
    // <-- Bunzl Custom Code
    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}> {siteMessage("Pricing_SignInForPrice")}</Typography>
            </StyledWrapper>
        );
    }

    // Starter Custom Code - >
    if (
        isInvalidPrice &&
        showInvalidPriceMessage &&
        !productHasPrice("productInfo" in pricingData ? pricingData.productInfo : pricingData)
    ) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText}>{siteMessage("Pricing_At_Zero")}</Typography>
            </SectionWrapper>
        );
    }

    if (!canUserSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}>{userCannotSeePricesMessage()}</Typography>
            </StyledWrapper>
        );
    }
    // <-- Starter Custom Code

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper}>
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    salePriceLabel={
                        productContextModel.product?.salePriceLabel ||
                        cartLineModel?.salePriceLabel ||
                        translate("Sale Price:")
                    }
                />
                {/* Starter Moved Code - isInvalidPrice && showInvalidPriceMessage */}
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper}>
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                    />
                </StyledWrapper>
            )}
            {/* Starter Custom Code */}
            {/* {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )} */}
            {/* Starter Custom Code */}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    const showPriceTypeSetting = useSettingsCollection()?.bunzlSettings?.showPriceType;
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    // Store Custom Code - >
    const { priceFactor, priceType, unitOfMeasure: unitOfMeasureDisplay } = pricing?.additionalResults || {};
    let priceFactorText = "";
    if (unitOfMeasureDisplay !== undefined) {
        priceFactorText = ` /${Number(priceFactor) > 1 ? ` ${priceFactor} ` : ""} ${unitOfMeasureDisplay}`;
    }

    const showTag = showPriceTypeSetting && priceType && priceType !== "List" && priceType !== "Others";

    // <-- Bunzl Custom Code

    return (
        <>
            {pricing ? (
                <>
                    <StyledWrapper {...styles.tagWrapper}>
                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {salePriceLabelPrefix}
                            {enableVat && displayWithVat
                                ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                            {priceFactorText}
                        </Typography>
                        {/* Bunzl Deleted Code */}

                        {/* Store Custom Code - > */}
                        {showTag && <PromotionalTag tag={priceType} />}
                    </StyledWrapper>
                    {/* <-- Bunzl Custom Code */}
                </>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice_CurrencySymbol">
                    {currencySymbol}
                </Typography>
            )}

            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            /&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
